import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Select, Modal, Form, Input, message, Space } from 'antd';
import { customerApi, type CustomerDTO, type CustomerAddressDTO } from '../api/kfc';

const CustomerAddresses: React.FC = () => {
  const [customers, setCustomers] = useState<CustomerDTO[]>([]);
  const [selectedCode, setSelectedCode] = useState<string | undefined>();
  const [addresses, setAddresses] = useState<CustomerAddressDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingAddr, setEditingAddr] = useState<CustomerAddressDTO | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    customerApi.list()
      .then((res) => setCustomers(res.data))
      .catch(() => { setCustomers([]); message.warning('无法加载客户列表'); });
  }, []);

  useEffect(() => {
    if (!selectedCode) {
      setAddresses([]);
      return;
    }
    setLoading(true);
    customerApi.getByCode(selectedCode)
      .then((res) => setAddresses(res.data.addresses || []))
      .catch(() => { setAddresses([]); })
      .finally(() => setLoading(false));
  }, [selectedCode]);

  const openAdd = () => {
    setEditingAddr(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (a: CustomerAddressDTO, index: number) => {
    setEditingAddr({ ...a, _index: index } as CustomerAddressDTO & { _index?: number });
    form.setFieldsValue(a);
    setModalOpen(true);
  };

  const onFinish = () => {
    if (!selectedCode) return;
    form.validateFields().then((values) => {
      const cust = customers.find((c) => c.customerCode === selectedCode);
      if (!cust?.id) return;
      const list = [...(addresses || [])];
      const idx = (editingAddr as CustomerAddressDTO & { _index?: number })?._index;
      const newAddr = {
        label: values.label,
        remark: values.remark,
        contactName: values.contactName,
        companyName: values.companyName,
        email: values.email,
        phone: values.phone,
        address: values.address,
        postalCode: values.postalCode,
      };
      if (typeof idx === 'number' && idx >= 0) {
        list[idx] = newAddr;
      } else {
        list.push(newAddr);
      }
      customerApi.update(cust.id, { ...cust, addresses: list })
        .then(() => {
          message.success('保存成功');
          setModalOpen(false);
          customerApi.getByCode(selectedCode).then((r) => setAddresses(r.data.addresses || []));
        })
        .catch((e) => message.error(e.response?.data?.message || '保存失败'));
    });
  };

  const doRemove = (index: number) => {
    const cust = customers.find((c) => c.customerCode === selectedCode);
    if (!cust?.id) return;
    const list = addresses.filter((_, i) => i !== index);
    customerApi.update(cust.id, { ...cust, addresses: list })
      .then(() => {
        message.success('已删除');
        setAddresses(list);
      })
      .catch((e) => message.error(e.response?.data?.message || '删除失败'));
  };

  const cols = [
    { title: '地址标识', dataIndex: 'label', width: 120 },
    { title: '备注', dataIndex: 'remark', width: 140, ellipsis: true },
    { title: '联系人', dataIndex: 'contactName', width: 100 },
    { title: '公司名称', dataIndex: 'companyName', width: 140 },
    { title: '邮箱', dataIndex: 'email', width: 180 },
    { title: '电话', dataIndex: 'phone', width: 120 },
    { title: '地址', dataIndex: 'address', ellipsis: true },
    { title: '邮编', dataIndex: 'postalCode', width: 80 },
    {
      title: '操作',
      width: 120,
      render: (_: unknown, __: CustomerAddressDTO, index: number) => (
        <Space>
          <Button type="link" size="small" onClick={() => openEdit(addresses[index], index)}>编辑</Button>
          <Button type="link" size="small" danger onClick={() => doRemove(index)}>删除</Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Card title="客户地址">
        <Space style={{ marginBottom: 16 }}>
          <Select
            placeholder="选择客户"
            allowClear
            style={{ width: 220 }}
            options={customers.map((c) => ({ label: `${c.customerCode} - ${c.name}`, value: c.customerCode }))}
            value={selectedCode}
            onChange={setSelectedCode}
          />
          {selectedCode && (
            <Button type="primary" onClick={openAdd}>新增地址</Button>
          )}
        </Space>
        <Table
          loading={loading}
          dataSource={addresses}
          rowKey={(_, i) => String(i)}
          columns={cols}
          pagination={false}
        />
      </Card>
      <Modal title={editingAddr ? '编辑地址' : '新增地址'} open={modalOpen} onCancel={() => setModalOpen(false)} onOk={onFinish} destroyOnClose>
        <Form form={form} layout="vertical">
          <Form.Item name="label" label="地址标识" rules={[{ required: true, message: '请输入地址标识' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <Input />
          </Form.Item>
          <Form.Item name="contactName" label="联系人" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="companyName" label="公司名称">
            <Input />
          </Form.Item>
          <Form.Item name="email" label="邮箱">
            <Input />
          </Form.Item>
          <Form.Item name="phone" label="电话">
            <Input />
          </Form.Item>
          <Form.Item name="address" label="地址" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="postalCode" label="邮编">
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default CustomerAddresses;
